'use client';

import { type ReactNode, useState } from 'react';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { FeedbackModal, type FeedbackFormData } from './FeedbackModal';

export interface FeedbackButtonProps {
  /** Tooltip text */
  tooltip?: string;
  /** Accessible label */
  ariaLabel?: string;
  /** Modal title */
  modalTitle?: string;
  /** Whether to show screenshot upload option */
  showScreenshot?: boolean;
  /** Whether to show the feedback type selector */
  showTypeSelector?: boolean;
  /** Labels for the form (pass translations here) */
  labels?: FeedbackButtonLabels;
  /** Called when feedback is submitted */
  onSubmit: (data: FeedbackFormData) => Promise<void>;
  /** Pre-fill the email field (e.g. from authenticated user) */
  defaultEmail?: string;
}

export interface FeedbackButtonLabels {
  tooltip?: string;
  ariaLabel?: string;
  title?: string;
  typeLabel?: string;
  types?: Record<string, string>;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  descriptionMinLength?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  emailHelper?: string;
  screenshotLabel?: string;
  uploadScreenshot?: string;
  removeScreenshot?: string;
  fileTooLarge?: string;
  submit?: string;
  submitting?: string;
  success?: string;
  error?: string;
  cancel?: string;
}

const DEFAULT_LABELS: Required<FeedbackButtonLabels> = {
  tooltip: 'Send Feedback',
  ariaLabel: 'Send feedback',
  title: 'Send Feedback',
  typeLabel: 'Feedback Type',
  types: {
    bug: 'Bug Report',
    feature_request: 'Feature Request',
    question: 'Question',
    other: 'Other',
  },
  descriptionLabel: 'Description',
  descriptionPlaceholder: 'Tell us what happened or what you\'d like to see...',
  descriptionMinLength: 'Description must be at least 10 characters',
  emailLabel: 'Email (optional)',
  emailPlaceholder: 'your@email.com',
  emailHelper: "We'll only use this to follow up on your feedback",
  screenshotLabel: 'Screenshot (optional)',
  uploadScreenshot: 'Upload screenshot',
  removeScreenshot: 'Remove screenshot',
  fileTooLarge: 'File must be under 5MB',
  submit: 'Submit Feedback',
  submitting: 'Submitting...',
  success: 'Thank you for your feedback!',
  error: 'Failed to submit feedback. Please try again.',
  cancel: 'Cancel',
};

export function FeedbackButton({
  labels,
  showScreenshot = true,
  showTypeSelector = true,
  onSubmit,
  defaultEmail,
}: FeedbackButtonProps): ReactNode {
  const [open, setOpen] = useState(false);
  const mergedLabels = { ...DEFAULT_LABELS, ...labels };

  return (
    <>
      <Tooltip title={mergedLabels.tooltip}>
        <Fab
          color="primary"
          size="medium"
          aria-label={mergedLabels.ariaLabel}
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            insetInlineEnd: 24,
            zIndex: 1000,
          }}
        >
          <FeedbackIcon />
        </Fab>
      </Tooltip>
      <FeedbackModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={onSubmit}
        labels={mergedLabels}
        showScreenshot={showScreenshot}
        showTypeSelector={showTypeSelector}
        {...(defaultEmail ? { defaultEmail } : {})}
      />
    </>
  );
}

FeedbackButton.displayName = 'FeedbackButton';
